import { useState } from 'react';
import { musician } from '@/mocks/musician';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const categoryIconMap: Record<string, string> = {
  worship: 'ri-heart-3-line',
  vocals: 'ri-mic-line',
  theatre: 'ri-movie-line',
  guitar: 'ri-music-2-line',
  piano: 'ri-music-line',
};

const categoryAccentMap: Record<string, { bg: string; text: string; badge: string; badgeText: string }> = {
  worship: { bg: 'bg-accent-100', text: 'text-accent-600', badge: 'bg-accent-500', badgeText: 'text-background-50' },
  vocals: { bg: 'bg-primary-100', text: 'text-primary-600', badge: 'bg-primary-500', badgeText: 'text-background-50' },
  theatre: { bg: 'bg-secondary-100', text: 'text-secondary-600', badge: 'bg-secondary-500', badgeText: 'text-background-50' },
  guitar: { bg: 'bg-accent-100', text: 'text-accent-600', badge: 'bg-accent-500', badgeText: 'text-background-50' },
  piano: { bg: 'bg-primary-100', text: 'text-primary-600', badge: 'bg-primary-500', badgeText: 'text-background-50' },
};

const sectionBgClasses = [
  'gradient-section film-grain section-popout',
  'section-grey film-grain',
  'gradient-section-mid film-grain section-popout',
  'section-grey film-grain',
  'gradient-section-orange film-grain section-popout',
];

export default function Music() {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [activeTitle, setActiveTitle] = useState('');
  useScrollAnimation();

  const openVideo = (embedUrl: string, title: string) => {
    setActiveVideo(embedUrl);
    setActiveTitle(title);
  };

  const closeVideo = () => {
    setActiveVideo(null);
    setActiveTitle('');
  };

  return (
    <>
      <title>Media &amp; Performances | Jackson Lacefield | Tenor &amp; Worship Musician</title>
      <meta name="description" content="Watch worship performances, classical works, original compositions, and multi-instrumental covers by Jackson Lacefield — tenor, violinist, pianist, and worship leader." />
      <meta name="keywords" content="Jackson Lacefield, media, worship performances, classical tenor, violin, piano, guitar, original compositions, church music" />
      <link rel="canonical" href="https://www.jacksonlacefield.com/music" />
      <meta property="og:title" content="Media &amp; Performances | Jackson Lacefield | Tenor &amp; Worship Musician" />
      <meta property="og:description" content="Watch worship performances, classical works, and original compositions by Jackson Lacefield." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.jacksonlacefield.com/music" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Media &amp; Performances | Jackson Lacefield" />
      <meta name="twitter:description" content="Watch worship performances, classical works, and original compositions by Jackson Lacefield." />
      <main className="min-h-screen bg-background-50">
      {/* ── Page Header ── */}
      <section className="page-header gradient-section-orange film-grain">
        <div className="section-container text-center relative z-10">
          <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4 scroll-animate">
            Media
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-3 scroll-animate">
            Watch &amp; Listen
          </h1>
          <p className="font-body text-sm md:text-base text-foreground-500 max-w-lg mx-auto scroll-animate">
            Worship performances, classical works, and original compositions across every genre I serve.
          </p>
        </div>
      </section>

      {/* ── Video Player Overlay ── */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-foreground-950/90 flex items-center justify-center p-4 md:p-8" onClick={closeVideo}>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading text-lg text-background-50">{activeTitle}</p>
              <button onClick={closeVideo} className="w-8 h-8 flex items-center justify-center text-background-50 hover:text-background-50/80 transition-colors cursor-pointer" aria-label="Close video">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-foreground-900">
              <iframe
                src={activeVideo}
                title={activeTitle}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* ── Category Sections ── */}
      {musician.mediaCategories.map((category, catIndex) => {
        const accent = categoryAccentMap[category.id] || categoryAccentMap.worship;
        const sectionBg = sectionBgClasses[catIndex % sectionBgClasses.length];

        return (
          <section
            key={category.id}
            id={`media-${category.id}`}
            className={`py-14 md:py-24 ${sectionBg}`}
          >
            <div className="section-container">
              <div className="mb-8 md:mb-12 scroll-animate">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-xl ${accent.bg} flex items-center justify-center ${accent.text}`}>
                    <i className={`${categoryIconMap[category.id] || 'ri-music-2-line'} text-lg`}></i>
                  </div>
                  <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500">
                    {category.label}
                  </span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">
                  {category.title}
                </h2>
                <p className="font-body text-sm text-foreground-500">
                  {category.subtitle}
                </p>
              </div>

              <div className={`grid grid-cols-1 ${category.items.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-2xl'} gap-4 md:gap-6`}>
                {category.items.map((item, i) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      if (item.type === 'video' && item.embedUrl) {
                        openVideo(item.embedUrl, item.title);
                      }
                    }}
                    className={`media-card group relative aspect-video bg-foreground-950 film-grain ${item.type === 'coming' ? 'cursor-default' : 'cursor-pointer'}`}
                    style={{ transitionDelay: `${i * 0.1}s` }}
                  >
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="w-full h-full object-cover object-center transition-all duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>

                    <div className="absolute inset-0 flex items-center justify-center">
                      {item.type === 'video' ? (
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-background-50/90 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:bg-accent-500">
                          <i className="ri-play-fill text-xl sm:text-2xl text-foreground-900 ml-0.5 group-hover:text-background-50 transition-colors"></i>
                        </div>
                      ) : (
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-background-50/15 backdrop-blur-sm flex items-center justify-center">
                          <i className="ri-time-line text-xl sm:text-2xl text-background-50/50"></i>
                        </div>
                      )}
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                      <span className={`inline-block ${accent.badge} text-white font-label text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full mb-2`}>
                        {item.context}
                      </span>
                      <p className="font-heading text-lg sm:text-xl md:text-2xl text-white font-medium">{item.title}</p>
                      {item.composer && (
                        <p className="font-body text-xs md:text-sm text-white/70 mt-1">{item.composer}</p>
                      )}
                      {category.id === 'worship' && item.description && (
                        <p className="font-body text-xs md:text-sm text-white/70 mt-1 hidden sm:block">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="section-container mt-10 md:mt-14">
              <div className="orange-divider">
                <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 text-accent-400">
                  <path d="M12 4 L12 20" stroke="currentColor" strokeWidth="1" />
                  <path d="M7 9 L17 9" stroke="currentColor" strokeWidth="1" />
                </svg>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── Compositions ── */}
      <section id="media-compositions" className="py-14 md:py-24 section-grey compositions-dark-cards film-grain">
        <div className="section-container">
          <div className="mb-8 md:mb-12 scroll-animate">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center text-accent-600">
                <i className="ri-quill-pen-line text-lg"></i>
              </div>
              <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500">
                Original Works
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">
              My Compositions
            </h2>
            <p className="font-body text-sm text-foreground-800 max-w-xl">
              Original choral works, worship songs, and hymn arrangements written from a heart of faith.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {musician.compositions.map((comp, i) => (
              <div
                key={comp.id}
                className="composition-card group"
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="inline-block bg-accent-100 text-accent-700 font-label text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full mb-2">
                      {comp.type}
                    </span>
                    <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-1">
                      {comp.title}
                    </h3>
                    <p className="font-label text-sm text-accent-500">{comp.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className="font-heading text-3xl md:text-4xl font-light text-accent-400/40 group-hover:text-accent-400/60 transition-colors duration-500">
                      {comp.year}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── YouTube Link ── */}
      <section className="py-12 md:py-16 gradient-section-orange film-grain">
        <div className="section-container text-center scroll-animate">
          <div className="orange-divider mb-8">
            <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 text-accent-200">
              <path d="M12 4 L12 20" stroke="currentColor" strokeWidth="1" />
              <path d="M7 9 L17 9" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
          <a
            href={musician.youtubeChannelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-accent-500 text-white px-6 py-3 rounded-full font-label text-sm tracking-wide hover:bg-accent-600 transition-all duration-500 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-youtube-line text-lg"></i>
            Visit My YouTube Channel
          </a>
        </div>
      </section>
    </main>
    </>
  );
}